import CIcon from '@coreui/icons-react';
import { CCol, CFormGroup } from '@coreui/react';
import GridTable from '@nadavshaar/react-grid-table';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { v4 as generateRandomId } from 'uuid';

import { controller } from '../../core/Controllers/OrmController';
import { OrmStoreType } from '../../core/Stores/DirectoryStore';
import { OrmUserStoreType } from '../../core/Stores/OrmUserStore';
import NatControlPanel from '../../shared/Components/NatControlPanel';
import { ChildrenPropsType } from '../../shared/Components/NatDocumentFormContainer';
import NatHeaderCollapseFormTemplate from '../../shared/Components/NatHeaderCollapseFormTemplate';
// import NatAclMapping from '../../shared/Components/NatAclMapping';
import { dateTimeFormatter } from '../../shared/Formatters/DateTimeFormatter';
import { numberRelationFormatter } from '../../shared/Formatters/NumberRelationFormatter';
import { percentFormatter } from '../../shared/Formatters/PercentFormatter';
import { priceFormatter } from '../../shared/Formatters/PriceFormatter';
import { quantityFormatter } from '../../shared/Formatters/QuantityFormatter';
import { relationFormatter } from '../../shared/Formatters/RelationFormatter';
import { stringFormatter } from '../../shared/Formatters/StringFormatter';
import NatNumberRelationInput from '../../shared/Inputs/NatNumberRelationInput';
import NatRelationInput from '../../shared/Inputs/NatRelationInput';
import NatTextarea from '../../shared/Inputs/NatTextarea';
import NatValueInput from '../../shared/Inputs/NatValueInput';
import { catalog } from '../../shared/support/Catalog';
import { models } from '../../shared/support/NatModels';
import { FormPropsType, SaleOfProductType } from '../../shared/support/modelTypes';
import { UseLocation } from '../../shared/support/useLocationHoC';
import { SaleOfProductFormStoreType } from './SaleOfProductFormStore';

interface PropsType extends FormPropsType {
	saleOfProductFormStore: SaleOfProductFormStoreType;
	childrenProps: ChildrenPropsType;
	fill(): void;
	resetData(): void;
	makeReactions(): void;
	isAlertOpen: boolean;
	toggleAlert(status: boolean): void;
	fillEmptyRelations(model: SaleOfProductType): SaleOfProductType;
}

interface StateType {
	columns: Array<any>;
	isModalOpen: boolean;
	setManagerColumns: Array<any>;
}
interface InjectedProps extends PropsType {
	directoryStore: OrmStoreType;
	userStore: OrmUserStoreType;
}

@inject('directoryStore', 'userStore')
@observer
class SaleOfProductForm extends Component<PropsType, StateType> {
	constructor(props: PropsType) {
		super(props);
		this.toggleSelection = this.toggleSelection.bind(this);
		this.state = {
			isModalOpen: false,
			setManagerColumns: [
				{
					id: 1,
					field: 'productId',
					label: 'Товар',
					width: '325px',
					cellRenderer: ({ data }: { data: any }) => {
						return (
							<div className="rgt-cell-inner ml-3 p-0">
								<NatRelationInput
									object={data}
									property="productId"
									relation="product"
									placeholder="Выберите товар"
									type="text"
									size="sm"
									inputClassName="text-left"
									formatter={relationFormatter()}
									pluralName="products"
									filterWhere={{
										deleted: false,
										elementType: 0,
										typeId: {
											nin: ['product_types.set']
										}
									}}
									include={['unit', 'validityPeriod']}
									autoComplete="off"
									hierarchicalSelect
									goTo={this.props.goTo}
								/>
							</div>
						);
					}
				},
				{
					id: 2,
					field: 'productSetId',
					label: 'Набор',
					width: '250px',
					checkCondition: (item: any) => {
						return catalog.checkProductSetCondition(item);
					},
					cellRenderer: ({ data }: { data: any }) => {
						return <div className="rgt-cell-inner">{!_.isEmpty(data.productSetId) || !_.isEmpty(data.productSetFeatureId) ? <CIcon name="cil-check-alt" size="lg" /> : ''}</div>;
					}
				},
				{
					id: 3,
					field: 'unitId',
					label: 'Ед.изм.',
					width: '250px',
					cellRenderer: ({ data }: { data: any }) => {
						return (
							<div className="rgt-cell-inner ml-3 p-0">
								<NatRelationInput
									object={data}
									property="unitId"
									relation="unit"
									placeholder="Выберите ед.изм."
									type="text"
									size="sm"
									inputClassName="text-left"
									formatter={relationFormatter()}
									pluralName="productUnits"
									apiMethod={(): any => {
										if (data.product !== undefined) {
											return controller.fetchUnitsByProduct({ product: data.product });
										} else {
											return new Promise((resolve) => {
												resolve([]);
											});
										}
									}}
									autoComplete="off"
									goTo={this.props.goTo}
								/>
							</div>
						);
					}
				},
				{
					id: 4,
					field: 'featureId',
					label: 'Характеристика',
					width: '325px',
					checkCondition: (item: any) => {
						return catalog.checkFeaturesCondition(item);
					},
					cellRenderer: ({ data }: { data: any }) => {
						return (
							<div className="rgt-cell-inner ml-3 p-0">
								{(() => {
									if (data.product !== undefined) {
										if (_.isEmpty(data.product.useFeatureOptionId) || data.product.useFeatureOptionId === 'use_feature_options.not_used') {
											return null;
										} else {
											return (
												<NatRelationInput
													object={data}
													property="featureId"
													relation="feature"
													placeholder="Выберите характеристику"
													type="text"
													size="sm"
													inputClassName="text-left"
													formatter={relationFormatter()}
													pluralName="productFeatures"
													autoComplete="off"
													apiMethod={(): any => {
														return controller.fetchFeaturesByProduct({ product: data.product });
													}}
													create={(pluralName, handlers) => {
														this.props.goTo(`/${pluralName}/new`, { ownerId: data.product.id, ownerType: 'OrmProduct' }, handlers);
													}}
												/>
											);
										}
									} else {
										return null;
									}
								})()}
							</div>
						);
					}
				},
				{
					id: 5,
					field: 'serialNumberId',
					label: 'Серийный номер',
					width: '250px',
					checkCondition: (item: any) => {
						return catalog.checkSerialNumbersCondition(item);
					},
					cellRenderer: ({ data }: { data: any }) => {
						return (
							<div className="rgt-cell-inner ml-3 p-0">
								{(() => {
									if (data.product !== undefined) {
										if (!data.product.useSerialNumbers) {
											return null;
										} else {
											return (
												<NatRelationInput
													object={data}
													property="serialNumberId"
													relation="serialNumber"
													placeholder="Выберите серийный номер"
													type="text"
													size="sm"
													inputClassName="text-left"
													formatter={relationFormatter(false, true)}
													pluralName="serialNumbers"
													filterWhere={{ deleted: false, or: [{ ownerId: data.productId }] }}
													autoComplete="off"
												/>
											);
										}
									} else {
										return null;
									}
								})()}
							</div>
						);
					}
				},
				{
					id: 6,
					field: 'quantity',
					label: 'Количество',
					width: '140px',
					cellRenderer: ({ data }: { data: any }) => {
						return (
							<div className="rgt-cell-inner ml-3 p-0 font__size__16">
								{(() => {
									if (data.productSet !== undefined) {
										if (data.productSet.setPriceCalculationMethodId !== 'product_set_price_calculation_methods.by_product_prices') {
											return quantityFormatter.renderValue(data.quantity);
										} else {
											return (
												<NatValueInput<number>
													object={data}
													property="quantity"
													placeholder="Введите кол-во"
													type="text"
													pattern="^([0-9]*[.,])?[0-9]*$"
													inputClassName="text-right"
													formatter={quantityFormatter}
													autoComplete="off"
													verticalSwitching
													validation={(object: any, value: any) => {
														return catalog.validateQuantityInput(object, value);
													}}
													size="sm"
													disabled
												/>
											);
										}
									} else {
										return quantityFormatter.renderValue(data.quantity);
									}
								})()}
							</div>
						);
					}
				},
				{
					id: 7,
					field: 'validityPeriodCount',
					label: 'Срок',
					checkCondition: (item: any) => {
						return catalog.checkValidityPeriodsCondition(item);
					},
					cellRenderer: ({ data }: { data: any }) => {
						return (
							<div className="rgt-cell-inner ml-3 p-0">
								{(() => {
									if (data.product !== undefined) {
										if (data.product.typeId === 'product_types.subscription' || data.product.typeId === 'product_types.subscription_option') {
											return (
												<NatNumberRelationInput<number>
													object={data}
													property="validityPeriodCount"
													relationProperty="validityPeriodId"
													relation="validityPeriod"
													placeholder="Введите период"
													type="text"
													size="sm"
													pattern="^([0-9]*[.,])?[0-9]*$"
													inputClassName="text-right"
													formatter={numberRelationFormatter}
													pluralName="enumerationItems"
													onlySelect={true}
													filterWhere={{ ownerId: 'periodicity' }}
													filterOrder={['priority ASC', `name.${this.injected.directoryStore.models.language} ASC`]}
													autoComplete="off"
													verticalSwitching
												/>
											);
										} else {
											return null;
										}
									} else {
										return null;
									}
								})()}
							</div>
						);
					}
				},
				{
					id: 8,
					field: 'price',
					label: 'Цена',
					cellRenderer: ({ data }: { data: any }) => {
						return (
							<div className="rgt-cell-inner ml-3 p-0 text-right font__size__16">
								{(() => {
									if (data.productSet !== undefined) {
										if (data.productSet.setPriceCalculationMethodId !== 'product_set_price_calculation_methods.by_product_prices') {
											return priceFormatter.renderValue(data.price);
										} else {
											return (
												<NatValueInput<number>
													object={data}
													property="price"
													placeholder="Введите цену"
													type="text"
													pattern="^([0-9]*[.,])?[0-9]*$"
													inputClassName="text-right"
													formatter={priceFormatter}
													autoComplete="off"
													verticalSwitching
													size="sm"
												/>
											);
										}
									} else {
										return priceFormatter.renderValue(data.price);
									}
								})()}
							</div>
						);
					}
				},
				{
					id: 9,
					field: 'amount',
					label: 'Сумма',
					cellRenderer: ({ data }: { data: any }) => {
						return (
							<div className="rgt-cell-inner ml-3 p-0 text-right font__size__16">
								{(() => {
									if (data.productSet !== undefined) {
										if (data.productSet.setPriceCalculationMethodId !== 'product_set_price_calculation_methods.by_product_prices') {
											return priceFormatter.renderValue(data.amount);
										} else {
											return (
												<NatValueInput<number>
													object={data}
													property="amount"
													placeholder="Введите сумму"
													type="text"
													pattern="^([0-9]*[.,])?[0-9]*$"
													inputClassName="text-right"
													formatter={priceFormatter}
													autoComplete="off"
													verticalSwitching
													size="sm"
													disabled
												/>
											);
										}
									} else {
										return priceFormatter.renderValue(data.amount);
									}
								})()}
							</div>
						);
					}
				},
				{
					id: 10,
					field: 'manualDiscountPercent',
					label: 'Ручной процент скидки',
					cellRenderer: ({ data }: { data: any }) => {
						return (
							<div className="rgt-cell-inner ml-3 p-0 text-right font__size__16">
								{(() => {
									if (data.productSet !== undefined) {
										if (data.productSet.setPriceCalculationMethodId !== 'product_set_price_calculation_methods.by_product_prices') {
											return priceFormatter.renderValue(data.manualDiscountPercent);
										} else {
											return (
												<NatValueInput<number>
													object={data}
													property="manualDiscountPercent"
													placeholder="Введите процент"
													type="text"
													pattern="^([0-9]*[.,])?[0-9]*$"
													inputClassName="text-right"
													formatter={percentFormatter}
													autoComplete="off"
													verticalSwitching
													size="sm"
												/>
											);
										}
									} else {
										return priceFormatter.renderValue(data.manualDiscountPercent);
									}
								})()}
							</div>
						);
					}
				},
				{
					id: 11,
					field: 'manualDiscountAmount',
					label: 'Ручная сумма скидки',
					cellRenderer: ({ data }: { data: any }) => {
						return (
							<div className="rgt-cell-inner ml-3 p-0 text-right font__size__16">
								{(() => {
									if (data.productSet !== undefined) {
										if (data.productSet.setPriceCalculationMethodId !== 'product_set_price_calculation_methods.by_product_prices') {
											return priceFormatter.renderValue(data.manualDiscountAmount);
										} else {
											return (
												<NatValueInput<number>
													object={data}
													property="manualDiscountAmount"
													placeholder="Введите цену"
													type="text"
													pattern="^([0-9]*[.,])?[0-9]*$"
													inputClassName="text-right"
													formatter={priceFormatter}
													autoComplete="off"
													verticalSwitching
													size="sm"
													disabled
												/>
											);
										}
									} else {
										return priceFormatter.renderValue(data.manualDiscountAmount);
									}
								})()}
							</div>
						);
					}
				},
				{
					id: 12,
					field: 'autoDiscountPercent',
					label: 'Автоматический процент скидки',
					cellRenderer: ({ data }: { data: any }) => {
						return (
							<div className="rgt-cell-inner ml-3 p-0 text-right font__size__16">
								{(() => {
									if (data.productSet !== undefined) {
										if (data.productSet.setPriceCalculationMethodId !== 'product_set_price_calculation_methods.by_product_prices') {
											return priceFormatter.renderValue(data.autoDiscountPercent);
										} else {
											return (
												<NatValueInput<number>
													object={data}
													property="autoDiscountPercent"
													placeholder="Введите процент"
													type="text"
													pattern="^([0-9]*[.,])?[0-9]*$"
													inputClassName="text-right"
													formatter={percentFormatter}
													autoComplete="off"
													verticalSwitching
													disabled
													size="sm"
												/>
											);
										}
									} else {
										return priceFormatter.renderValue(data.autoDiscountPercent);
									}
								})()}
							</div>
						);
					}
				},
				{
					id: 13,
					field: 'autoDiscountAmount',
					label: 'Автоматическая сумма скидки',
					cellRenderer: ({ data }: { data: any }) => {
						return (
							<div className="rgt-cell-inner ml-3 p-0 text-right font__size__16">
								{(() => {
									if (data.productSet !== undefined) {
										if (data.productSet.setPriceCalculationMethodId !== 'product_set_price_calculation_methods.by_product_prices') {
											return priceFormatter.renderValue(data.autoDiscountAmount);
										} else {
											return (
												<NatValueInput<number>
													object={data}
													property="autoDiscountAmount"
													placeholder="Введите цену"
													type="text"
													pattern="^([0-9]*[.,])?[0-9]*$"
													inputClassName="text-right"
													formatter={priceFormatter}
													autoComplete="off"
													verticalSwitching
													size="sm"
													disabled
												/>
											);
										}
									} else {
										return priceFormatter.renderValue(data.autoDiscountAmount);
									}
								})()}
							</div>
						);
					}
				}
			],
			columns: [
				{
					id: 'checkbox',
					pinned: true,
					className: '',
					width: '54px',
					minResizeWidth: 0,
					maxResizeWidth: null,
					resizable: false,
					visible: true,
					headerCellRenderer: () => {
						return <div></div>;
					},
					cellRenderer: ({ data }: { onChange: any; value: any; data: any }) => {
						return (
							<div className="rgt-cell-inner">
								<input
									type="checkbox"
									id="checkbox"
									onChange={() => {
										this.props.childrenProps.saveSelected(data);
									}}
								/>
							</div>
						);
					}
				},
				{
					id: 1,
					field: 'product',
					label: 'Товар',
					width: '325px',
					cellRenderer: ({ data }: { data: any }) => {
						if (!_.isEmpty(data.productSetId) || !_.isEmpty(data.productSetFeatureId)) {
							return <div className="rgt-cell-inner p-0 ml-2 font__size__16">{catalog.renderCatalogName(data.product)}</div>;
						} else {
							return (
								<div className="rgt-cell-inner ml-3 p-0">
									<NatRelationInput
										object={data}
										property="productId"
										relation="product"
										placeholder="Выберите товар"
										type="text"
										size="sm"
										inputClassName="text-left"
										formatter={relationFormatter()}
										pluralName="products"
										filterWhere={{
											deleted: false,
											elementType: 0,
											typeId: {
												nin: ['product_types.set']
											}
										}}
										include={['unit', 'validityPeriod']}
										autoComplete="off"
										hierarchicalSelect
										goTo={this.props.goTo}
									/>
								</div>
							);
						}
					}
				},
				{
					id: 2,
					field: 'productSetId',
					label: 'Набор',
					width: '250px',
					checkCondition: (item: any) => {
						return catalog.checkProductSetCondition(item);
					},
					cellRenderer: ({ data }: { data: any }) => {
						return <div className="rgt-cell-inner">{!_.isEmpty(data.productSetId) || !_.isEmpty(data.productSetFeatureId) ? <CIcon name="cil-check-alt" size="lg" /> : ''}</div>;
					}
				},
				{
					id: 3,
					field: 'unit',
					label: 'Ед.изм.',
					width: '250px',
					cellRenderer: ({ data }: { data: any }) => {
						if (!_.isEmpty(data.productSetId) || !_.isEmpty(data.productSetFeatureId)) {
							return <div className="rgt-cell-inner p-0 ml-2 font__size__16">{catalog.renderCatalogName(data.unit)}</div>;
						} else {
							return (
								<div className="rgt-cell-inner ml-3 p-0">
									<NatRelationInput
										object={data}
										property="unitId"
										relation="unit"
										placeholder="Выберите ед.изм."
										type="text"
										size="sm"
										inputClassName="text-left"
										formatter={relationFormatter()}
										pluralName="productUnits"
										apiMethod={(): any => {
											if (data.product !== undefined) {
												return controller.fetchUnitsByProduct({ product: data.product });
											} else {
												return new Promise((resolve) => {
													resolve([]);
												});
											}
										}}
										autoComplete="off"
										goTo={this.props.goTo}
									/>
								</div>
							);
						}
					}
				},
				{
					id: 4,
					field: 'feature',
					label: 'Характеристика',
					width: '325px',
					checkCondition: (item: any) => {
						return catalog.checkFeaturesCondition(item);
					},
					cellRenderer: ({ data }: { data: any }) => {
						if (!_.isEmpty(data.productSetId) || !_.isEmpty(data.productSetFeatureId)) {
							return (
								<div className="rgt-cell-inner p-0 ml-2 font__size__16">
									{(() => {
										if (data.product !== undefined) {
											if (_.isEmpty(data.product.useFeatureOptionId) || data.product.useFeatureOptionId === 'use_feature_options.not_used') {
												return null;
											} else {
												return catalog.renderCatalogName(data.feature);
											}
										} else {
											return null;
										}
									})()}
								</div>
							);
						} else {
							return (
								<div className="rgt-cell-inner ml-3 p-0">
									{(() => {
										if (data.product !== undefined) {
											if (_.isEmpty(data.product.useFeatureOptionId) || data.product.useFeatureOptionId === 'use_feature_options.not_used') {
												return null;
											} else {
												return (
													<NatRelationInput
														object={data}
														property="featureId"
														relation="feature"
														placeholder="Выберите характеристику"
														type="text"
														size="sm"
														inputClassName="text-left"
														formatter={relationFormatter()}
														pluralName="productFeatures"
														autoComplete="off"
														apiMethod={(): any => {
															return controller.fetchFeaturesByProduct({ product: data.product });
														}}
														create={(pluralName, handlers) => {
															this.props.goTo(`/${pluralName}/new`, { ownerId: data.product.id, ownerType: 'OrmProduct' }, handlers);
														}}
													/>
												);
											}
										} else {
											return null;
										}
									})()}
								</div>
							);
						}
					}
				},
				{
					id: 5,
					field: 'serialNumber',
					label: 'Серийный номер',
					width: '250px',
					checkCondition: (item: any) => {
						return catalog.checkSerialNumbersCondition(item);
					},
					cellRenderer: ({ data }: { data: any }) => {
						if (!_.isEmpty(data.productSetId) || !_.isEmpty(data.productSetFeatureId)) {
							return (
								<div className="rgt-cell-inner p-0 ml-2 font__size__16">
									{(() => {
										if (data.product !== undefined) {
											if (!data.product.useSerialNumbers) {
												return null;
											} else {
												return catalog.renderCatalogName(data.serialNumber);
											}
										} else {
											return null;
										}
									})()}
								</div>
							);
						} else {
							return (
								<div className="rgt-cell-inner ml-3 p-0">
									{(() => {
										if (data.product !== undefined) {
											if (!data.product.useSerialNumbers) {
												return null;
											} else {
												return (
													<NatRelationInput
														object={data}
														property="serialNumberId"
														relation="serialNumber"
														placeholder="Выберите серийный номер"
														type="text"
														size="sm"
														inputClassName="text-left"
														formatter={relationFormatter(false, true)}
														pluralName="serialNumbers"
														filterWhere={{ deleted: false, or: [{ ownerId: data.productId }] }}
														autoComplete="off"
													/>
												);
											}
										} else {
											return null;
										}
									})()}
								</div>
							);
						}
					}
				},
				{
					id: 6,
					field: 'quantity',
					label: 'Количество',
					width: '140px',
					cellRenderer: ({ data }: { data: any }) => {
						if (!_.isEmpty(data.productSetId) || !_.isEmpty(data.productSetFeatureId)) {
							return <div className="rgt-cell-inner p-0 mr__4_8 font__size__16 text-right">{quantityFormatter.renderValue(data.quantity)}</div>;
						} else {
							return (
								<div className="rgt-cell-inner ml-3 p-0">
									<NatValueInput<number>
										object={data}
										property="quantity"
										placeholder="Введите кол-во"
										type="text"
										pattern="^([0-9]*[.,])?[0-9]*$"
										inputClassName="text-right"
										formatter={quantityFormatter}
										autoComplete="off"
										verticalSwitching
										size="sm"
									/>
								</div>
							);
						}
					}
				},
				{
					id: 7,
					field: 'validityPeriodCount',
					label: 'Срок',
					checkCondition: (item: any) => {
						return catalog.checkValidityPeriodsCondition(item);
					},
					cellRenderer: ({ data }: { data: any }) => {
						if (!_.isEmpty(data.productSetId) || !_.isEmpty(data.productSetFeatureId)) {
							return (
								<div className="rgt-cell-inner p-0 mr__4_8 font__size__16 text-right">
									{(() => {
										if (data.product !== undefined) {
											if (data.product.typeId === 'product_types.subscription' || data.product.typeId === 'product_types.subscription_option') {
												return numberRelationFormatter.renderValue(data.validityPeriodCount, data.validityPeriod);
											} else {
												return null;
											}
										} else {
											return null;
										}
									})()}
								</div>
							);
						} else {
							return (
								<div className="rgt-cell-inner ml-3 p-0">
									{(() => {
										if (data.product !== undefined) {
											if (data.product.typeId === 'product_types.subscription' || data.product.typeId === 'product_types.subscription_option') {
												return (
													<NatNumberRelationInput<number>
														object={data}
														property="validityPeriodCount"
														relationProperty="validityPeriodId"
														relation="validityPeriod"
														placeholder="Введите период"
														type="text"
														size="sm"
														pattern="^([0-9]*[.,])?[0-9]*$"
														inputClassName="text-right"
														formatter={numberRelationFormatter}
														pluralName="enumerationItems"
														onlySelect={true}
														filterWhere={{ ownerId: 'periodicity' }}
														filterOrder={['priority ASC', `name.${this.injected.directoryStore.models.language} ASC`]}
														autoComplete="off"
														verticalSwitching
													/>
												);
											} else {
												return null;
											}
										} else {
											return null;
										}
									})()}
								</div>
							);
						}
					}
				},
				{
					id: 8,
					field: 'price',
					label: 'Цена',
					cellRenderer: ({ data }: { data: any }) => {
						if (!_.isEmpty(data.productSetId) || !_.isEmpty(data.productSetFeatureId)) {
							return <div className="rgt-cell-inner p-0 mr__4_8 font__size__16 text-right">{priceFormatter.renderValue(data.price)}</div>;
						} else {
							return (
								<div className="rgt-cell-inner ml-3 p-0">
									<NatValueInput<number>
										object={data}
										property="price"
										placeholder="Введите цену"
										type="text"
										pattern="^([0-9]*[.,])?[0-9]*$"
										inputClassName="text-right"
										formatter={priceFormatter}
										autoComplete="off"
										verticalSwitching
										size="sm"
									/>
								</div>
							);
						}
					}
				},
				{
					id: 9,
					field: 'amount',
					label: 'Сумма',
					cellRenderer: ({ data }: { data: any }) => {
						if (!_.isEmpty(data.productSetId) || !_.isEmpty(data.productSetFeatureId)) {
							return <div className="rgt-cell-inner p-0 mr__4_8 font__size__16 text-right">{priceFormatter.renderValue(data.amount)}</div>;
						} else {
							return (
								<div className="rgt-cell-inner ml-3 p-0">
									<NatValueInput<number>
										object={data}
										property="amount"
										placeholder="Введите сумму"
										type="text"
										pattern="^([0-9]*[.,])?[0-9]*$"
										inputClassName="text-right"
										formatter={priceFormatter}
										autoComplete="off"
										verticalSwitching
										size="sm"
										disabled
									/>
								</div>
							);
						}
					}
				},
				{
					id: 10,
					field: 'manualDiscountPercent',
					label: 'Ручной процент скидки',
					cellRenderer: ({ data }: { data: any }) => {
						if (!_.isEmpty(data.productSetId) || !_.isEmpty(data.productSetFeatureId)) {
							return <div className="rgt-cell-inner p-0 mr__4_8 font__size__16 text-right">{percentFormatter.renderValue(data.manualDiscountPercent)}</div>;
						} else {
							return (
								<div className="rgt-cell-inner ml-3 p-0">
									<NatValueInput<number>
										object={data}
										property="manualDiscountPercent"
										placeholder="Введите процент"
										type="text"
										pattern="^([0-9]*[.,])?[0-9]*$"
										inputClassName="text-right"
										formatter={percentFormatter}
										autoComplete="off"
										verticalSwitching
										size="sm"
									/>
								</div>
							);
						}
					}
				},
				{
					id: 11,
					field: 'manualDiscountAmount',
					label: 'Ручная сумма скидки',
					cellRenderer: ({ data }: { data: any }) => {
						if (!_.isEmpty(data.productSetId) || !_.isEmpty(data.productSetFeatureId)) {
							return <div className="rgt-cell-inner p-0 mr__4_8 font__size__16 text-right">{priceFormatter.renderValue(data.manualDiscountAmount)}</div>;
						} else {
							return (
								<div className="rgt-cell-inner ml-3 p-0">
									<NatValueInput<number>
										object={data}
										property="manualDiscountAmount"
										placeholder="Введите цену"
										type="text"
										pattern="^([0-9]*[.,])?[0-9]*$"
										inputClassName="text-right"
										formatter={priceFormatter}
										autoComplete="off"
										verticalSwitching
										size="sm"
										disabled
									/>
								</div>
							);
						}
					}
				},
				{
					id: 12,
					field: 'autoDiscountPercent',
					label: 'Автоматический процент скидки',
					cellRenderer: ({ data }: { data: any }) => {
						if (!_.isEmpty(data.productSetId) || !_.isEmpty(data.productSetFeatureId)) {
							return <div className="rgt-cell-inner p-0 mr__4_8 font__size__16 text-right">{percentFormatter.renderValue(data.autoDiscountPercent)}</div>;
						} else {
							return (
								<div className="rgt-cell-inner ml-3 p-0">
									<NatValueInput<number>
										object={data}
										property="autoDiscountPercent"
										placeholder="Введите процент"
										type="text"
										pattern="^([0-9]*[.,])?[0-9]*$"
										inputClassName="text-right"
										formatter={percentFormatter}
										autoComplete="off"
										verticalSwitching
										disabled
										size="sm"
									/>
								</div>
							);
						}
					}
				},
				{
					id: 13,
					field: 'autoDiscountAmount',
					label: 'Автоматическая сумма скидки',
					cellRenderer: ({ data }: { data: any }) => {
						if (!_.isEmpty(data.productSetId) || !_.isEmpty(data.productSetFeatureId)) {
							return <div className="rgt-cell-inner p-0 mr__4_8 font__size__16 text-right">{priceFormatter.renderValue(data.autoDiscountAmount)}</div>;
						} else {
							return (
								<div className="rgt-cell-inner ml-3 p-0">
									<NatValueInput<number>
										object={data}
										property="autoDiscountAmount"
										placeholder="Введите цену"
										type="text"
										pattern="^([0-9]*[.,])?[0-9]*$"
										inputClassName="text-right"
										formatter={priceFormatter}
										autoComplete="off"
										verticalSwitching
										size="sm"
										disabled
									/>
								</div>
							);
						}
					}
				}
			]
		};
	}
	get injected() {
		return this.props as InjectedProps;
	}
	toggleSelection(status: boolean) {
		this.setState({
			isModalOpen: status
		});
	}
	render() {
		let productItemTemplate = {
			productSetId: null,
			productSetFeatureId: null,
			amount: 0,
			autoDiscountAmount: 0,
			autoDiscountPercent: 0,
			manualDiscountAmount: 0,
			manualDiscountPercent: 0,
			bonusPointIncome: 0,
			bonusPointIncomeConverted: 0,
			bonusPointOutcome: 0,
			bonusPointOutcomeConverted: 0,
			featureId: null,
			organizationId: this.props.saleOfProductFormStore.model.organizationId,
			price: 0,
			productId: null,
			quantity: 1,
			serialNumberId: null,
			unitId: null,
			id: '',
			priceTypeId: this.props.saleOfProductFormStore.model.priceTypeId,
			warehouseId: this.props.saleOfProductFormStore.model.warehouseId,
			validityPeriodCount: 0,
			validityPeriodId: 'periodicity.month',
			validityPeriod: {
				id: 'periodicity.month',
				keyId: 'month',
				ownerId: 'periodicity',
				name: { ru: 'Месяц' },
				description: null,
				priority: 6,
				predefined: true,
				predefinedName: 'PeriodicityMonth',
				predefinedVersion: 0
			},
			feature: {
				deleted: false,
				displayName: null,
				name: { ru: '' },
				ownerId: null,
				ownerType: null,
				priority: null
			},
			product: {
				accountingOptionId: 'product_accounting_options.standart',
				area: 0,
				areaUnitId: null,
				article: null,
				caption: null,
				deleted: false,
				description: null,
				displayName: null,
				elementType: 0,
				giftCardValidityExpiredAt: null,
				giftCardValidityPeriodCount: 0,
				giftCardValidityPeriodId: 'periodicity.year',
				giftCardValidityTypeId: 'gift_card_validity_types.without_restrictions',
				giftCardValue: 0,
				height: 0,
				heightUnitId: null,
				kindId: null,
				labelTemplateId: null,
				length: 0,
				lengthUnitId: null,
				name: { ru: '' },
				organizationId: null,
				packageSetId: null,
				parentId: null,
				primaryImageId: null,
				priority: null,
				supplierId: null,
				typeId: 'product_types.goods',
				unitId: null,
				useArea: false,
				useCustomLabelTemplate: false,
				useFeatureOptionId: 'use_feature_options.not_used',
				useGiftCardPartially: false,
				useHeight: false,
				useLength: false,
				usePackageOptionId: 'use_package_options.not_used',
				useSerialNumbers: false,
				useVolume: false,
				useWeight: false,
				useWidth: false,
				volume: 0,
				volumeUnitId: null,
				weight: 0,
				weightCanManuallySet: false,
				weightUnitId: null,
				width: 0,
				widthUnitId: null
			},
			unit: {
				baseUnitId: null,
				deleted: false,
				displayName: null,
				elementType: 0,
				name: { ru: '' },
				ownerId: null,
				ownerType: null,
				packageTypeId: 'unit_package_types.final',
				parentId: null,
				priority: null,
				quantityOfOwnerUnitIncludedInPackage: 0,
				quantityOfPackageIncludedInOwnerUnit: 1,
				quantityOfParentPackageIncludedInPackage: 0,
				typeId: null
			},
			serialNumber: {
				serialNumber: '',
				gtin: null,
				deleted: false,
				displayName: null,
				name: { ru: '' },
				ownerId: null,
				ownerType: null,
				priority: null
			}
		};
		return (
			<NatHeaderCollapseFormTemplate
				makeReactions={this.props.makeReactions}
				store={this.props.saleOfProductFormStore}
				productSelection
				isAlertOpen={this.props.isAlertOpen}
				toggleAlert={this.props.toggleAlert}
				productItemTemplate={productItemTemplate}
				selectionColumns={this.state.columns}
				navTabs={[{ name: 'Товары' }, { name: 'Примечание' }, { name: 'Дополнительно' }]}
				isAclModalOpen={this.props.childrenProps.isAclModalOpen}
				toggleAclModal={this.props.childrenProps.toggleAclModal}
				isRelatedDocumentsModalOpen={this.props.childrenProps.isRelatedDocumentsModalOpen}
				toggleNatRelatedDocumentsModal={this.props.childrenProps.toggleNatRelatedDocumentsModal}
				isProductSetEditingModalOpen={this.props.childrenProps.isProductSetEditingModalOpen}
				toggleSetEditing={this.props.childrenProps.toggleNatSetEditing}
				isRevealCompositionOfSet
				toggleSequenceNumberChangingAlert={this.props.childrenProps.toggleSequenceNumberChangingAlert}
				isSequenceNumberChangingModalOpen={this.props.childrenProps.isSequenceNumberChangingModalOpen}
				productSetEditing
				setManagerColumns={this.state.setManagerColumns}
				tabsContent={(toggleSelection: (status: boolean) => void) => {
					return [
						<>
							<NatControlPanel
								buttonItemList={[
									{
										title: 'ДОБАВИТЬ',
										onClick: () => {
											this.props.childrenProps.pushEmptyTemplate(productItemTemplate);
										},
										withoutIcon: true,
										disabled: false
									},
									{
										title: 'УДАЛИТЬ',
										onClick: () => {
											this.props.childrenProps.deleteProductItems(this.props.saleOfProductFormStore.selectedItems);
										},
										withoutIcon: true,
										disabled: this.props.saleOfProductFormStore.selectedItems.length === 0
									},
									{
										title: 'ПОДБОР',
										onClick: () => {
											toggleSelection(true);
										},
										withoutIcon: true,
										disabled: false
									},
									{
										title: 'ВВЕРХ',
										onClick: () => {
											catalog.moveItems(this.props.saleOfProductFormStore.selectedItems, this.props.saleOfProductFormStore.model.productItems as Array<any>, -1);
										},
										withoutIcon: true,
										disabled: this.props.saleOfProductFormStore.selectedItems.length === 0
									},
									{
										title: 'ВНИЗ',
										onClick: () => {
											catalog.moveItems(this.props.saleOfProductFormStore.selectedItems, this.props.saleOfProductFormStore.model.productItems as Array<any>, 1);
										},
										withoutIcon: true,
										disabled: this.props.saleOfProductFormStore.selectedItems.length === 0
									},
									{
										title: 'ЦЕНЫ И СКИДКИ',
										onClick: () => {},
										disabled: false,
										children: [
											{
												title: 'Рассчитать автоматические скидки',
												onClick: () => {
													this.props.saleOfProductFormStore.setValue(this.props.saleOfProductFormStore, 'isLoading', true);
													let model = _.cloneDeep(this.props.saleOfProductFormStore.model);
													model.productItemList = model.productItems;
													controller
														.apply(model)
														.then((data) => {
															if (!_.isEmpty(data)) {
																const model = this.props.fillEmptyRelations(data);
																model.productItems = model.productItemList;
																this.props.saleOfProductFormStore.setValue(this.props.saleOfProductFormStore, 'model', model);
																this.props.makeReactions();
															}
															this.props.saleOfProductFormStore.setValue(this.props.saleOfProductFormStore, 'isLoading', false);
														})
														.catch((error) => {
															catalog.handleNatError(error);
															this.props.saleOfProductFormStore.setValue(this.props.saleOfProductFormStore, 'isLoading', false);
														});
												}
											}
										]
									}
								]}
								inputList={[
									<div className="d-flex mr-4 search__height ml-1" key={generateRandomId()}>
										<NatValueInput<string> object={this.props.saleOfProductFormStore} property="searchText" placeholder="Поиск" type="text" formatter={stringFormatter()} size="sm" autoComplete="off" mobile />
									</div>
								]}
							/>
							<div className="nat__form__table__wrapper m-4 h-100">
								<form className="nat__form__table">
									<GridTable
										columns={catalog.checkColumnVisibility(catalog.convertCellMethods(this.state.columns), _.get(this.props.saleOfProductFormStore.model, 'productItems', []))}
										rows={this.props.saleOfProductFormStore.model.productItems}
										isPaginated={false}
										texts={{ search: 'Поиск:', totalRows: 'Кол-во строк:', columnVisibility: 'Отображение столбцов', noResults: 'Элементы отсутствуют' }}
										showColumnVisibilityManager={false}
										showRowsInformation={false}
										onColumnsChange={() => {}}
										highlightSearch={false}
										isVirtualScroll={false}
										onRowClick={({ data, column }: { data: any; column: any }) => {
											if (column.id !== 'checkbox') {
												if (!_.isEmpty(data.productSetId) || !_.isEmpty(data.productSetFeatureId)) {
													this.props.saleOfProductFormStore.setValue(this.props.saleOfProductFormStore, 'productItem', data);
													this.props.childrenProps.toggleNatSetEditing(true);
												}
											}
										}}
										showSearch={false}
										searchText={this.props.saleOfProductFormStore.searchText}
									/>
								</form>
							</div>
						</>,
						<>
							<div className="nat__form__table__wrapper mx-4 mb-4 h-100">
								<div className="nat__form__table">
									<NatTextarea
										object={this.props.saleOfProductFormStore.model}
										property="note"
										label=""
										placeholder="Введите примечание"
										type="text"
										inputColumnClassName="default__nat__card__body__height px-0"
										labelColumnSettings="12"
										labelColumnClassName="text-left align-self-center"
										wrapperClassName="default__nat__card__body__height"
										inputClassName="default__nat__card__body__height nat__scrollbar text-left"
										formatter={stringFormatter()}
										row={false}
										size="sm"
									/>
								</div>
							</div>
						</>,
						<>
							<div className="nat__form__table__wrapper mx-4 mb-4 h-100">
								<div className="nat__form__table">
									<CCol lg="12">
										<CFormGroup row className="mb-0">
											<CCol md="4">
												<NatRelationInput
													object={this.props.saleOfProductFormStore.model}
													property="consignorId"
													relation="consignor"
													placeholder="Выберите грузоотправителя"
													type="text"
													size="sm"
													formatter={relationFormatter()}
													pluralName="partners"
													filterWhere={{ deleted: false }}
													autoComplete="off"
													label="Грузоотправитель"
													mobile
													goTo={this.props.goTo}
												/>
											</CCol>
											<CCol md="4">
												<NatRelationInput
													object={this.props.saleOfProductFormStore.model}
													property="consigneeId"
													relation="consignee"
													placeholder="Выберите грузополучателя"
													type="text"
													size="sm"
													formatter={relationFormatter()}
													pluralName="partners"
													filterWhere={{ deleted: false }}
													autoComplete="off"
													label="Грузополучатель"
													mobile
													goTo={this.props.goTo}
												/>
											</CCol>
										</CFormGroup>
										<CFormGroup row className="mb-0">
											<CCol md="4">
												<NatRelationInput
													object={this.props.saleOfProductFormStore.model}
													property="partnerBankAccountId"
													relation="partnerBankAccount"
													placeholder="Выберите счет"
													type="text"
													size="sm"
													formatter={relationFormatter()}
													pluralName="partnerBankAccounts"
													onlySelect={true}
													filterWhere={{ deleted: false }}
													autoComplete="off"
													label="Банковский счет контрагента"
													mobile
													goTo={this.props.goTo}
												/>
											</CCol>
											<CCol md="4">
												<NatRelationInput
													object={this.props.saleOfProductFormStore.model}
													property="organizationBankAccountId"
													relation="organizationBankAccount"
													placeholder="Выберите счет"
													type="text"
													size="sm"
													formatter={relationFormatter()}
													pluralName="organizationBankAccounts"
													onlySelect={true}
													filterWhere={{ deleted: false }}
													autoComplete="off"
													label="Банковский счет организации"
													mobile
													goTo={this.props.goTo}
												/>
											</CCol>
										</CFormGroup>
										<CFormGroup row className="mb-0">
											<CCol md="4">
												<NatValueInput<string>
													object={this.props.saleOfProductFormStore.model}
													property="sequenceNumber"
													placeholder="Введите номер"
													type="text"
													formatter={stringFormatter()}
													size="sm"
													autoComplete="off"
													label="Номер"
													mobile
													onClick={() => {
														if (!this.props.saleOfProductFormStore.isSequenceNumberChangedManually) {
															this.props.childrenProps.toggleSequenceNumberChangingAlert(true);
														}
													}}
												/>
											</CCol>
										</CFormGroup>
									</CCol>
								</div>
							</div>
						</>
					];
				}}
				fields={
					<>
						<CCol lg="12">
							<CFormGroup row className="mb-0">
								<CCol md="4">
									<NatValueInput<Date> object={this.props.saleOfProductFormStore.model} property="periodAt" placeholder="Введите дату" type="datetime-local" step="1" formatter={dateTimeFormatter} size="sm" label="Дата документа" />
								</CCol>
								<CCol md="4">
									<NatRelationInput
										object={this.props.saleOfProductFormStore.model}
										property="organizationId"
										relation="organization"
										placeholder="Выберите организацию"
										type="text"
										size="sm"
										formatter={relationFormatter()}
										pluralName="organizations"
										filterWhere={{ deleted: false }}
										autoComplete="off"
										invalid={_.isEmpty(this.props.saleOfProductFormStore.model.organizationId)}
										invalidFeedback="Элемент не выбран"
										include={['bankAccount']}
										label="Организация"
										mobile
										goTo={this.props.goTo}
									/>
								</CCol>
							</CFormGroup>
							<CFormGroup row className="mb-0">
								<CCol md="4">
									<NatRelationInput
										object={this.props.saleOfProductFormStore.model}
										property="partnerId"
										relation="partner"
										placeholder="Выберите контрагента"
										type="text"
										size="sm"
										formatter={relationFormatter()}
										pluralName="partners"
										filterWhere={{ deleted: false, elementType: 0 }}
										autoComplete="off"
										invalid={_.isEmpty(this.props.saleOfProductFormStore.model.partnerId)}
										invalidFeedback="Элемент не выбран"
										include={['bankAccount']}
										label="Контрагент"
										mobile
										hierarchicalSelect
										goTo={this.props.goTo}
									/>
								</CCol>
								<CCol md="4">
									<NatRelationInput
										object={this.props.saleOfProductFormStore.model}
										property="warehouseId"
										relation="warehouse"
										placeholder="Выберите склад"
										type="text"
										size="sm"
										formatter={relationFormatter()}
										pluralName="warehouses"
										filterWhere={{ deleted: false, elementType: 0 }}
										autoComplete="off"
										label="Склад"
										mobile
										goTo={this.props.goTo}
									/>
								</CCol>
							</CFormGroup>
							<CFormGroup row className="mb-0">
								<CCol md="4">
									<NatRelationInput
										object={this.props.saleOfProductFormStore.model}
										property="contractId"
										relation="contract"
										placeholder="Выберите договор"
										type="text"
										size="sm"
										formatter={relationFormatter()}
										pluralName="partnerContracts"
										apiMethod={(): any => {
											if (!_.isEmpty(this.props.saleOfProductFormStore.model.partner)) {
												return controller.fetchAvailableContracts(this.props.saleOfProductFormStore.model.partner);
											} else {
												return Promise.reject(new Error('Контрагент не выбран!'));
											}
										}}
										disabled={_.isEmpty(this.props.saleOfProductFormStore.model.partnerId)}
										create={(pluralName, handlers) => {
											this.props.goTo(`/${pluralName}/new?partnerId=${this.props.saleOfProductFormStore.model.partnerId}`, handlers);
										}}
										autoComplete="off"
										label="Договор"
										mobile
									/>
								</CCol>
								<CCol md="4">
									<NatRelationInput
										object={this.props.saleOfProductFormStore.model}
										property="priceTypeId"
										relation="priceType"
										placeholder="Выберите тип цены"
										type="text"
										size="sm"
										formatter={relationFormatter()}
										pluralName="priceTypes"
										filterWhere={{ deleted: false }}
										autoComplete="off"
										label="Тип цены"
										mobile
										goTo={this.props.goTo}
									/>
								</CCol>
							</CFormGroup>
							<CFormGroup row className="mb-0">
								<CCol md="4">
									<NatValueInput<number>
										object={this.props.saleOfProductFormStore.model}
										property="amount"
										placeholder="Введите сумму"
										type="text"
										pattern="^([0-9]*[.,])?[0-9]*$"
										formatter={priceFormatter}
										size="sm"
										disabled={true}
										label="Сумма"
									/>
								</CCol>
								<CCol md="4">
									<NatRelationInput
										object={this.props.saleOfProductFormStore.model}
										property="ownerId"
										relation="owner"
										placeholder="Выберите документ-основание"
										type="text"
										size="sm"
										formatter={relationFormatter()}
										pluralName={this.props.saleOfProductFormStore.model.ownerType ? models[this.props.saleOfProductFormStore.model.ownerType].plural : ''}
										filterWhere={{ deleted: false }}
										autoComplete="off"
										label="Основание"
										mobile
										disabled
										renderName={(model: any) => {
											if (this.props.saleOfProductFormStore.model.ownerType) {
												return catalog.renderDocumentName(model, this.props.saleOfProductFormStore.model.ownerType);
											} else {
												return '';
											}
										}}
										goTo={this.props.goTo}
									/>
								</CCol>
							</CFormGroup>
						</CCol>
					</>
				}
				controlButtons={[
					{
						title: 'ОК',
						onClick: (e: any) => {
							this.props.childrenProps
								.post(e)
								.then(() => {
									this.props.removeNavigationRoute(this.props.navigationRoute);
								})
								.catch((error) => {
									catalog.handleNatError(error);
									this.props.saleOfProductFormStore.setValue(this.props.saleOfProductFormStore, 'isLoading', false);
								});
						},
						disabled: this.props.saleOfProductFormStore.isLoading
					},
					{
						title: 'ПРОВЕСТИ',
						onClick: (e: any) => {
							this.props.childrenProps.makePosted(e, 'post', true);
						},
						disabled: this.props.saleOfProductFormStore.isLoading
					},
					{
						title: 'ЗАПИСАТЬ',
						onClick: (e: any) => {
							if (this.props.saleOfProductFormStore.model.posted) {
								this.props.childrenProps.makePosted(e, 'post', true);
							} else {
								this.props.childrenProps.makePosted(e, 'write', true);
							}
						},
						disabled: this.props.saleOfProductFormStore.isLoading
					},
					{
						title: 'ОТМЕНА',
						onClick: () => {
							this.props.removeNavigationRoute(this.props.navigationRoute);
						}
					},
					{
						title: this.props.saleOfProductFormStore.model.deleted ? 'ОТМЕНИТЬ УДАЛЕНИЕ' : 'УДАЛИТЬ',
						onClick: () => {
							this.props.childrenProps.makeDeleted();
						},
						disabled: this.props.match.params.elementId === 'new'
					},
					{
						title: 'ЕЩЕ',
						onClick: () => {},
						disabled: this.props.match.params.elementId === 'new',
						children: [
							{
								title: 'Управление доступом',
								onClick: () => {
									this.props.childrenProps.toggleAclModal(true);
								}
							},
							{
								title: 'Связанные документы',
								onClick: () => {
									this.props.childrenProps.toggleNatRelatedDocumentsModal(true);
								}
							},
							{
								title: 'Отменить проведение',
								onClick: (e: any) => {
									this.props.childrenProps.makePosted(e, 'unpost', true);
								}
							}
						]
					}
				]}
			/>
		);
	}
}

export default UseLocation(withRouter(SaleOfProductForm));
